/**
 * FitPlus Platform AI chat client (anonymous session UUIDs, no ID token).
 */

const DEFAULT_BASE = 'http://localhost:9050';
const SESSION_LIST_KEY = 'fitplusAiChatSessions';

function apiBase() {
  return (process.env.VUE_APP_FITPLUS_PLATFORM_API_BASE || DEFAULT_BASE).replace(/\/$/, '');
}

function buildUrl(path) {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return `${apiBase()}${normalized}`;
}

async function requestJson(path, init) {
  const res = await fetch(buildUrl(path), {
    ...init,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...((init && init.headers) || {}),
    },
  });

  if (!res.ok) {
    let detail = `${res.status} ${res.statusText}`;
    try {
      const body = await res.json();
      if (body && body.detail) {
        detail = typeof body.detail === 'string' ? body.detail : JSON.stringify(body.detail);
      }
    } catch (e) {
      /* ignore */
    }
    throw new Error(`AI-Chat API: ${detail}`);
  }

  return res.json();
}

export function createAiChatSession(locale = 'de') {
  return requestJson('/api/v1/chat/sessions', {
    method: 'POST',
    body: JSON.stringify({ locale }),
  });
}

export function getAiChatSession(sessionId) {
  return requestJson(`/api/v1/chat/sessions/${encodeURIComponent(sessionId)}`);
}

/**
 * @param {string} sessionId
 * @param {string} message
 * @param {{ userId?: string|null, profile?: object|null }} [options]
 */
export function sendAiChatMessage(sessionId, message, options) {
  const userId = options && options.userId ? options.userId : null;
  const profile = options && options.profile ? options.profile : null;
  return requestJson(`/api/v1/chat/sessions/${encodeURIComponent(sessionId)}/messages`, {
    method: 'POST',
    body: JSON.stringify({
      message,
      ...(userId ? { user_id: userId } : {}),
      ...(profile ? { profile } : {}),
    }),
  });
}

/**
 * Compact Firestore userInformation for the chat context payload.
 * @param {object|null|undefined} info
 */
export function buildAiChatProfilePayload(info) {
  if (!info || typeof info !== 'object') {
    return null;
  }

  const profile = {};
  if (info.height != null && info.height !== '') {
    profile.height = Number(info.height);
  }
  if (info.weight != null && info.weight !== '') {
    profile.weight = Number(info.weight);
  }
  if (info.gender != null && info.gender !== '') {
    profile.gender = Number(info.gender);
  }

  if (info.birthday) {
    if (typeof info.birthday === 'object' && info.birthday.seconds != null) {
      profile.birthday = { seconds: info.birthday.seconds };
      const birth = new Date(0);
      birth.setUTCSeconds(info.birthday.seconds);
      const today = new Date();
      let age = today.getFullYear() - birth.getFullYear();
      const m = today.getMonth() - birth.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
        age -= 1;
      }
      if (age >= 0 && age <= 120) {
        profile.age = age;
      }
    } else {
      profile.birthday = info.birthday;
    }
  }

  if (Array.isArray(info.sports_types) && info.sports_types.length) {
    profile.sports_types = info.sports_types.map((st) => {
      if (st && typeof st === 'object') {
        return {
          id: st.id,
          name: st.name || st.title || undefined,
        };
      }
      return { name: String(st) };
    });
  }

  if (Array.isArray(info.customSportsTypes) && info.customSportsTypes.length) {
    profile.customSportsTypes = info.customSportsTypes.map((st) => {
      if (st && typeof st === 'object') {
        return st.name || st.title || String(st);
      }
      return String(st);
    });
  }

  return Object.keys(profile).length ? profile : null;
}

export function resolveAiChatSessionTitle(options) {
  const opts = options || {};
  const fromMessage = (opts.sessionTitle || '').trim();
  if (fromMessage) return fromMessage;

  const fromSession = (opts.sessionResponseTitle || '').trim();
  if (fromSession) return fromSession;

  const items = opts.referencedItems || [];
  if (items.length === 1 && items[0] && items[0].title) {
    return String(items[0].title).trim();
  }

  const userText = (opts.userMessage || '').trim().replace(/\s+/g, ' ');
  if (!userText) return undefined;
  return userText.length > 42 ? `${userText.slice(0, 42)}…` : userText;
}

/** Local comfort list of recent session ids (backend remains source of truth). */
export function listLocalAiChatSessions() {
  try {
    const raw = window.localStorage.getItem(SESSION_LIST_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
}

export function rememberLocalAiChatSession(session) {
  const list = listLocalAiChatSessions().filter((s) => s.sessionId !== session.sessionId);
  list.unshift({
    sessionId: session.sessionId,
    title: session.title || 'Neuer Chat',
    lastMessagePreview: session.lastMessagePreview || '',
    updatedAt: session.updatedAt || new Date().toISOString(),
  });
  window.localStorage.setItem(SESSION_LIST_KEY, JSON.stringify(list.slice(0, 40)));
}

export function removeLocalAiChatSession(sessionId) {
  const list = listLocalAiChatSessions().filter((s) => s.sessionId !== sessionId);
  window.localStorage.setItem(SESSION_LIST_KEY, JSON.stringify(list));
}

/**
 * Map a content reference to an in-app route.
 * @param {{ canonical_id?: string, type?: string, detail_url?: string }} ref
 */
export function routeForContentReference(ref) {
  if (!ref) return null;
  if (ref.detail_url && ref.detail_url.startsWith('/')) {
    return ref.detail_url;
  }
  const id = String(ref.canonical_id || '');
  const match = id.match(/^(workout|article|playlist|challenge):(\d+)$/i);
  if (!match) return null;
  const type = match[1].toLowerCase();
  const pageId = match[2];
  if (type === 'workout') return `/workout/${pageId}`;
  if (type === 'playlist') return `/playlist/start?playlist_id=${pageId}`;
  if (type === 'article') return `/articles/${pageId}`;
  if (type === 'challenge') return `/fitnessplan/challenges?challengeId=${pageId}`;
  return null;
}
